import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, setCsrfToken, setUnauthorizedHandler } from './api/client';
import type { Bootstrap, Session } from './api/types';
import { EntryDialog } from './components/EntryDialog';
import { PeriodPanel } from './components/PeriodPanel';
import { BudgetsPage } from './pages/Budgets';
import { DashboardPage } from './pages/Dashboard';
import { DataPage } from './pages/Data';
import { HistoryPage } from './pages/History';
import { InsightsPage } from './pages/Insights';
import { PairingPage } from './pages/Pairing';
import { RecurringPage } from './pages/Recurring';
import { SettingsPage } from './pages/Settings';
import { AppProvider, useApp, type Page } from './state';

type Boot = { status: 'loading' } | { status: 'pairing'; message?: string } | { status: 'error'; message: string } | { status: 'ready'; session: Session; catalog: Bootstrap };

/** 세션 확인 → (없으면 페어링) → bootstrap. 웹은 서버 데이터만 원본으로 쓴다(localStorage 미사용). */
export function Root() {
  const [boot, setBoot] = useState<Boot>({ status: 'loading' });

  const start = useCallback(async () => {
    try {
      const session = await api.get<{ data: Session }>('/session');
      setCsrfToken(session.data.csrfToken ?? null);
      const catalog = await api.get<{ data: Bootstrap }>('/bootstrap');
      setBoot({ status: 'ready', session: session.data, catalog: catalog.data });
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) setBoot({ status: 'pairing' });
      else setBoot({ status: 'error', message: error instanceof Error ? error.message : '알 수 없는 오류' });
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setCsrfToken(null);
      setBoot({ status: 'pairing', message: '기기 인증이 만료되었거나 폐기되었습니다. 다시 연결해주세요.' });
    });
    void start();
  }, [start]);

  if (boot.status === 'loading') return <div className="boot">불러오는 중…</div>;
  if (boot.status === 'pairing') return <PairingPage message={boot.message} onPaired={start} />;
  if (boot.status === 'error')
    return (
      <div className="boot">
        <p>{boot.message}</p>
        <button className="primary" onClick={() => void start()}>
          다시 시도
        </button>
      </div>
    );
  return (
    <AppProvider session={boot.session} initialCatalog={boot.catalog}>
      <Shell />
    </AppProvider>
  );
}

const NAV: { page: Page; label: string; icon: string }[] = [
  { page: 'dashboard', label: '대시보드', icon: '◫' },
  { page: 'history', label: '전체 내역', icon: '☷' },
  { page: 'recurring', label: '반복 항목', icon: '↻' },
  { page: 'budgets', label: '예산', icon: '◎' },
  { page: 'insights', label: 'AI 분석', icon: '✧' },
  { page: 'settings', label: '항목 설정', icon: '⚙' },
  { page: 'data', label: '데이터', icon: '⇅' }
];

const TITLES: Record<Page, string> = {
  dashboard: '이 기간, 어떻게 썼을까요?',
  history: '내 소비의 작은 기록들',
  recurring: '반복되는 수입·지출도, 차곡',
  budgets: '한 달 예산 정하기',
  insights: '기록이 알려주는 소비 습관',
  settings: '내 가계부의 항목 설정',
  data: '내보내기 · 가져오기'
};

const WITH_PERIOD: Page[] = ['dashboard', 'history', 'recurring', 'insights'];

function Shell() {
  const { page, navigate, session, openEntry, period } = useApp();
  const daysLeft = Math.floor((Date.parse(session.expiresAt) - Date.now()) / 86_400_000);
  return (
    <>
      <aside>
        <a
          className="brand"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            navigate('dashboard');
          }}
        >
          ▧ 차곡<span>나를 알아가는 작은 기록</span>
        </a>
        <nav>
          {NAV.map((item) => (
            <button key={item.page} className={page === item.page ? 'active' : ''} onClick={() => navigate(item.page)}>
              {item.icon}　{item.label}
            </button>
          ))}
        </nav>
        <div className="side-note">
          조금 적고,
          <br />
          더 잘 알아가기.<small>나만의 가계부</small>
        </div>
      </aside>
      <main>
        <header>
          <span>MY PERSONAL LEDGER</span>
          <button className="primary" onClick={() => openEntry({ kind: 'expense' })}>
            ＋ 수입·지출 기록
          </button>
        </header>
        <div className="heading">
          <div>
            <p className="eyebrow">차곡차곡 쌓이는 나의 일상</p>
            <h1>{TITLES[page]}</h1>
          </div>
          {WITH_PERIOD.includes(page) && (
            <span className="range-summary">
              {period.start} ~ {period.end}
            </span>
          )}
        </div>
        {daysLeft <= 14 && (
          <p className="notice warn">
            이 브라우저의 기기 인증이 {Math.max(daysLeft, 0)}일 후 만료됩니다. 서버에서 새 페어링 코드를 발급해 다시 연결해주세요.
          </p>
        )}
        {WITH_PERIOD.includes(page) && <PeriodPanel />}
        {page === 'dashboard' && <DashboardPage />}
        {page === 'history' && <HistoryPage />}
        {page === 'recurring' && <RecurringPage />}
        {page === 'budgets' && <BudgetsPage />}
        {page === 'insights' && <InsightsPage />}
        {page === 'settings' && <SettingsPage />}
        {page === 'data' && <DataPage />}
        <footer>차곡 · 나의 소비를 이해하는 가장 작은 습관</footer>
      </main>
      <EntryDialog />
    </>
  );
}
